import { getHybridApp, getPrefixedRoute } from '@vmos2/core';
import { createElement, useCallback, useEffect } from 'react';
import type { ComponentType, ReactElement } from 'react';
import { useRoute as useRouteLib } from 'react-router5';

type Features = Record<string, string>;

type Route = {
  component?: ComponentType;
  features?: Features[];
  key?: string;
  name: string;
  path: string;
};

const makeRouteComponent = (routes: Route[], currentRoute: Route, features: Features) => {
  if (currentRoute && currentRoute.name) {
    const { name = '' } = currentRoute;
    const segments = name.split('.');

    const getRouteComponent = (config?: Route) => {
      if (config?.features) {
        const featureConfig = config.features.find(feature => features[feature.name]);

        if (featureConfig) {
          return featureConfig.component;
        }
      }

      return config?.component;
    };

    const routesWithComponents = segments.reduce(
      (acc, segment) => {
        const segmentPath = `${acc.segmentPath}${acc.segmentPath && '.'}${segment}`;
        const config = routes.find(route => route.name === segmentPath);

        if (config?.component) {
          acc.configs.push(config);
        }

        return { ...acc, segmentPath };
      },
      { configs: [] as Route[], segmentPath: '' }
    );

    const wrapperConfig = routesWithComponents.configs.shift();
    const component = getRouteComponent(wrapperConfig);

    if (component) {
      const childComponent = routesWithComponents.configs
        .reverse()
        .reduce((child: ReactElement | null, childRouteConfig) => {
          const nestedChildComponent = getRouteComponent(childRouteConfig);

          if (nestedChildComponent) {
            return createElement(nestedChildComponent, { key: childRouteConfig.key ?? childRouteConfig.name }, child);
          }

          return child;
        }, null);

      return createElement(component, null, childComponent);
    }
  }

  return null;
};

export const useRoute = () => {
  const { previousRoute, router, route } = useRouteLib();

  const navigateTo = useCallback(
    (to: string, nextParams: Record<string, string>, ...args: [Record<string, any>, () => void]) => {
      const { params } = router.getState() || {};

      return router.navigate(getPrefixedRoute(to), { ...params, ...nextParams }, ...args);
    },
    []
  );

  const getRouteComponent = useCallback(
    (features: Features) => {
      const { routes } = router.getDependencies();

      return makeRouteComponent(routes, route, features);
    },
    [route]
  );

  useEffect(() => {
    getHybridApp().then(hybridApp => {
      hybridApp?.emitEvent?.('urlChanged', { to: route.name, params: route.params }, true);
    });
  }, [route.name, route.params]);

  return { route, router, previousRoute, navigateTo, getRouteComponent };
};
